import { Trans, useTranslation } from 'react-i18next';
import { useState, useRef } from 'react';
import './contact-us.scss';
import CommonButton from '../../components/button/button';
import '../../components/header/header.scss';
import imgFon from '../../assets/images/contact-us/Frame 167.png';
import useDeviceDetection from '../../hooks/useDeviceDetection';
import FloatingLabelInput from '../../components/common/floating-label-input/floating-label-input';
import GotIt from './got-it';

const lines = <svg xmlns="http://www.w3.org/2000/svg" width="45" height="61" viewBox="0 0 45 61" fill="none">
    <path d="M31.7438 39.2335C24.2574 34.2099 16.5226 29.279 9.04459 24.5108L7.20149 23.3339C6.49847 22.8871 5.72215 22.5508 4.8205 22.1646C4.44263 22.0022 4.05279 21.8354 3.64637 21.6483C3.44832 21.7339 3.24137 21.8154 3.03811 21.891C2.56024 22.0724 1.90494 22.3197 1.78537 22.5808C1.76904 22.6196 1.75854 22.6619 1.75356 22.7111C1.70504 23.1905 2.18425 24.1922 2.428 24.6981C2.60736 25.0733 3.09447 25.3835 3.5263 25.6569L6.50425 27.5541C14.1487 32.4261 22.0555 37.4675 29.862 42.3652C31.0853 43.1317 32.0181 43.4248 32.6306 43.2322C32.9928 43.1198 33.271 42.8251 33.4825 42.3311C33.677 41.882 32.7416 39.8965 31.7444 39.2274L31.7438 39.2335Z" fill="#49DF23" />
    <path d="M21.6203 2.3263L21.6199 2.32938C21.5159 3.51107 21.7674 4.73803 22.2632 5.45263C27.0299 12.3464 32.684 20.4415 38.4867 28.235C38.8365 28.7051 39.8398 28.8842 40.8124 29.0603C41.0626 29.1042 41.3155 29.1515 41.565 29.2016C41.5659 28.9782 41.5702 28.752 41.5738 28.5319C41.5937 27.5994 41.6136 26.6358 41.2451 26.0738C36.1223 18.3429 30.9582 10.9277 25.2246 2.72831C24.8803 2.23396 24.3432 1.83497 23.6679 1.32885C23.4311 1.15276 23.1799 0.965932 22.9214 0.759688C22.7752 0.915654 22.6269 1.06203 22.489 1.19711C22.0748 1.60848 21.6467 2.03402 21.6178 2.31984L21.6203 2.3263Z" fill="#49DF23" />
    <path d="M18.083 55.7437C17.9245 55.8674 17.7658 55.9941 17.6046 56.1144C17.7427 56.2836 17.8771 56.4587 18.0121 56.6276C18.54 57.302 19.0841 57.9997 19.6701 58.1925C21.3083 58.7247 23.0351 58.9647 24.5597 59.178C25.123 59.2567 25.6497 59.3287 26.1227 59.4107C26.9863 59.4981 27.7794 59.6063 28.5296 59.7102C30.119 59.9269 31.4909 60.1154 32.7946 59.9959C33.5498 59.9264 34.3176 59.4267 35.1352 58.8947C35.3247 58.7711 35.5207 58.6449 35.7163 58.5219C35.5435 58.2964 35.3778 58.0623 35.2149 57.8316C34.6687 57.0622 34.0492 56.1891 33.5028 56.0966C29.5466 55.4292 25.1994 55.0047 19.8275 54.7653C19.3514 54.7451 18.7103 55.2515 18.0864 55.741L18.083 55.7437Z" fill="#49DF23" />
</svg>;

function ContactUs() {
    const [t] = useTranslation();
    const deviceType = useDeviceDetection();
    const isRtl = true;

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
    });

    // Store error messages (if any) as strings
    const [errors, setErrors] = useState({});
    const refs = {
        name: useRef(null),
        email: useRef(null),
        message: useRef(null),
    };

    // Initially, no email is sent
    const [isSent, setIsSent] = useState(false);
    // Loading indicator state
    const [isLoading, setIsLoading] = useState(false);
    // Notification state for bottom bar messages
    const [notification, setNotification] = useState(null);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Reset the error for the field when user changes it
        setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = "שכחתם את השם... 😎";
        }

        const emailTrimmed = formData.email.trim();
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        if (!emailTrimmed || !emailRegex.test(emailTrimmed)) {
            newErrors.email = "יש מצב שכתובת המייל לא תקינה?🤔";
        }

        setErrors(newErrors);

        // Focus the first invalid input, if any
        const firstInvalid = Object.keys(newErrors)[0];
        if (firstInvalid) {
            refs[firstInvalid].current?.focus();
            return false;
        }

        return true;
    };

    const sentFailed = (error) => {

        console.error('Error sending email:', error);
        setNotification({ message: t('contact-us.errors.sent-failed'), type: "error" });
    }

    const handleSubmit = async () => {
        setNotification(null)
        if (!validate()) return;

        setIsLoading(true);
        try {
            const response = await fetch('https://www.lnet.co.il/bo/api/Mail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setIsSent(true);
                // setNotification({ message: "הודעתך נשלחה בהצלחה", type: "success" });
                // setFormData({ name: '', email: '', message: '' });
            } else {
                sentFailed(null)
            }
        } catch (error) {
            sentFailed(error)
        } finally {
            setIsLoading(false);
            setTimeout(() => setNotification(null), 6000);
        }
    };

    const contactUsDetails = (
        <div className='contact-us-details'>
            {t('contact-us.address')} <br />
            {t('contact-us.phone')}<br />
            <Trans i18nKey={'contact-us.mail'}>
                <span></span>
            </Trans>
        </div>
    );

    const messageInputs = <div className='messege-inputs'>
        <FloatingLabelInput
            required
            id="name"
            name="name"
            label={t('contact-us.name')}
            value={formData.name}
            onChange={handleChange}
            rtl={isRtl}
            ref={refs.name}
            isInvalid={!!errors.name}
            errorMessage={errors.name}
        />
        <FloatingLabelInput
            required
            id="email"
            name="email"
            label={t('contact-us.email')}
            value={formData.email}
            onChange={handleChange}
            rtl={isRtl}
            ref={refs.email}
            type='email'
            isInvalid={!!errors.email}
            errorMessage={errors.email}
        />
        <FloatingLabelInput
            id="message"
            name="message"
            label={t('contact-us.message')}
            required={false}
            value={formData.message}
            onChange={handleChange}
            autoComplete={false}
            rtl={isRtl}
            ref={refs.message}
            isInvalid={false}
        />
    </div>;

    const submitButton = <CommonButton
        text={t(isLoading ? 'contact-us.loading' : 'contact-us.ctaButton')}
        onClickFunc={handleSubmit}
        disabled={isLoading}
    />;

    return (
        <div className='contact-us-components'>
            {deviceType?.isMobile ?
                <> <img src={imgFon} alt="" className="no-flip" />

                    <div className='information-details'>
                        <div className='contact-information'>
                            {messageInputs}
                            <div className='flex'>
                                {submitButton}
                                {lines}
                            </div>
                        </div>
                        {contactUsDetails}
                    </div>
                </>
                : <>
                    <div className='information-details'>
                        <div className='contact-information'>
                            {messageInputs}

                            {submitButton}
                            {contactUsDetails}
                        </div>

                        <img src={imgFon} alt="" className="no-flip" />
                    </div>
                </>}
            {notification && (
                <div className={`notification text-run ${notification.type}`}>
                    {notification.message}
                </div>)}

            {isSent && <GotIt isMobile={deviceType?.isMobile} />}
        </div>
    );
}

export default ContactUs;
